import { getCardIdFromQuery, isValidCardId, parseCardIdFromInput } from '../lib/card-id';

export interface SetupFormData {
  cardId: string;
  network: string;
  coin: string;
  host: string;
  address: string;
}

interface SetupFormProps {
  values: SetupFormData;
  onChange: (values: SetupFormData) => void;
  error: string | null;
  disabled?: boolean;
  cardIdLocked?: boolean;
}

/** Form matching Cronyl design — card ID + wallet fields */
export function SetupForm({ values, onChange, error, disabled, cardIdLocked }: SetupFormProps) {
  const set = (key: keyof SetupFormData, value: string) => {
    onChange({ ...values, [key]: value });
  };

  const handleCardIdChange = (raw: string) => {
    const parsed = parseCardIdFromInput(raw);
    set('cardId', parsed ?? raw.trim());
  };

  return (
    <form className="setup-form" onSubmit={(e) => e.preventDefault()} noValidate>
      <div className="field">
        <label htmlFor="cardId">Card ID</label>
        <input
          id="cardId"
          value={values.cardId}
          onChange={(e) => handleCardIdChange(e.target.value)}
          placeholder="Scan your card or enter ID"
          autoComplete="off"
          disabled={disabled || cardIdLocked}
          readOnly={cardIdLocked}
        />
        <p className="hint">
          {cardIdLocked
            ? 'Detected from your NFC card scan.'
            : 'Tap your Cronyl Card — the ID appears automatically, or paste your card link.'}
        </p>
      </div>

      <div className="field">
        <label htmlFor="network">Network</label>
        <input
          id="network"
          value={values.network}
          onChange={(e) => set('network', e.target.value)}
          placeholder="e.g. bitcoin, ethereum, xrp"
          autoComplete="off"
          disabled={disabled}
        />
      </div>

      <div className="field">
        <label htmlFor="coin">Coin / Asset</label>
        <input
          id="coin"
          value={values.coin}
          onChange={(e) => set('coin', e.target.value)}
          placeholder="e.g. BTC, ETH, USDC"
          autoComplete="off"
          disabled={disabled}
        />
      </div>

      <div className="field">
        <label htmlFor="host">Wallet Host</label>
        <input
          id="host"
          value={values.host}
          onChange={(e) => set('host', e.target.value)}
          placeholder="e.g. Ledger, Trust Wallet"
          autoComplete="off"
          disabled={disabled}
        />
      </div>

      <div className="field">
        <label htmlFor="address">Wallet Address</label>
        <input
          id="address"
          value={values.address}
          onChange={(e) => set('address', e.target.value)}
          placeholder="paste address"
          autoComplete="off"
          spellCheck={false}
          disabled={disabled}
        />
      </div>

      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
    </form>
  );
}

export function validateSetupForm(values: SetupFormData): { ok: true } | { ok: false; msg: string } {
  const cardId = (values.cardId || '').trim();
  if (!cardId) return { ok: false, msg: 'Please scan your Cronyl Card or enter the Card ID.' };
  if (!isValidCardId(cardId)) return { ok: false, msg: 'Invalid Card ID format.' };
  if (!values.network.trim()) return { ok: false, msg: 'Please enter the network.' };
  if (!values.coin.trim()) return { ok: false, msg: 'Please enter the coin or asset.' };
  if (!values.host.trim()) return { ok: false, msg: 'Please enter the wallet host.' };
  if (!values.address.trim()) return { ok: false, msg: 'Please enter the wallet address.' };
  return { ok: true };
}

export { getCardIdFromQuery };
